import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, IndianRupee, Coins, Smartphone, ArrowRight, Lock, Unlock, Trash, Calculator,
  CheckCircle2, AlertCircle, Wallet, TrendingUp, Clock, ShieldAlert, RefreshCcw, X, Upload, Camera,
  ArrowDownCircle, ArrowUpCircle, Receipt, ChevronDown, ChevronUp,
} from "lucide-react";
import { api, formatApiError, API_BASE } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";

const DENOMS = [2000, 500, 200, 100, 50, 20, 10, 5, 2, 1];
const EXPENSE_CATEGORIES = ["Milk", "Raw Material", "Packaging", "Transport", "Delivery", "Cleaning", "Maintenance", "Shop Purchase", "Staff Expense", "Other"];
const OWNER_REASONS = ["Owner Collection", "Bank Deposit", "Cash Transfer", "Other"];

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminCash() {
  const { user, isMaster } = useAuth();
  const master = isMaster();
  const [day, setDay] = useState(null);
  const [health, setHealth] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showExpense, setShowExpense] = useState(false);
  const [showOwner, setShowOwner] = useState(null); // "removal" | "addition" | "upi_transfer" | "upi_addition"
  const [showTimeline, setShowTimeline] = useState(false);

  const load = async () => {
    try {
      const [{ data: d }, { data: h }] = await Promise.all([
        api.get("/admin/cash/today"),
        api.get("/admin/cash/health"),
      ]);
      setDay(d); setHealth(h);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const patchDay = async (patch) => {
    setSaving(true);
    try { const { data } = await api.put(`/admin/cash/day/${day.date}`, patch); setDay(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const deleteTx = async (id) => {
    if (!master) return toast.error("Master privileges required to delete");
    if (!window.confirm("Delete this transaction?")) return;
    try { const { data } = await api.delete(`/admin/cash/transactions/${id}`); setDay(data.day); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const closeDay = async () => {
    if (!window.confirm(`Close ${day.date}? Tomorrow will open with these balances:\n\nCash: ${fmt(day.expected_closing_cash)}\nUPI: ${fmt(day.closing_upi)}`)) return;
    try { const { data } = await api.post(`/admin/cash/day/${day.date}/close`); setDay(data); toast.success("Day closed. Tomorrow's opening set."); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reopenDay = async () => {
    try { const { data } = await api.post(`/admin/cash/day/${day.date}/reopen`); setDay(data); toast.success("Day reopened"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const fixCarry = async () => {
    if (!window.confirm("Re-carry today's openings from yesterday's closing?")) return;
    try { await api.post(`/admin/cash/day/${day.date}/carry-forward`); toast.success("Opening balances re-carried"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const setDenom = (d, val) => {
    const denominations = { ...(day.denominations || {}), [String(d)]: Math.max(0, parseInt(val || 0)) };
    setDay({ ...day, denominations });
  };
  const saveDenoms = () => patchDay({ denominations: day.denominations, coins_extra: Number(day.coins_extra || 0) });

  if (!day) return <div className="text-brand-charcoal/50 py-10 text-center">Loading…</div>;

  const locked = day.status === "closed";
  const healthIssues = health?.issues || [];
  const canCloseUi = day.can_close && !locked;

  return (
    <div data-testid="admin-cash-page" data-role={user?.role_level || "master"} className="pb-16">
      {/* ---------- HEADER ---------- */}
      <header className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Cash & Digital Control · Daily Ledger</div>
          <h1 className="editorial-h1 text-4xl">Today &mdash; {day.date}</h1>
          <div className="mt-1 text-sm text-brand-charcoal/60 flex items-center gap-2">
            <StatusBadge status={day.status} />
            {locked && day.closed_at && <span className="text-xs">Closed at {new Date(day.closed_at).toLocaleTimeString()} by {day.closed_by}</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {master && <Link to="/admin/cash/history" className="btn-pill btn-ghost text-xs" data-testid="cash-nav-history"><Clock size={11} /> Last 7 days</Link>}
          {master && <Link to="/admin/cash/audit" className="btn-pill btn-ghost text-xs" data-testid="cash-nav-audit"><TrendingUp size={11} /> Audit</Link>}
          {master && <Link to="/admin/cash/settings" className="btn-pill btn-ghost text-xs" data-testid="cash-nav-settings">Settings</Link>}
          {!locked ? (
            <button onClick={closeDay} disabled={!canCloseUi} className="btn-pill btn-dark text-xs disabled:opacity-40 disabled:cursor-not-allowed" data-testid="cash-close-day" title={canCloseUi ? "Close today" : "Fix all mismatches before closing"}>
              <Lock size={11} /> Close Day
            </button>
          ) : master ? (
            <button onClick={reopenDay} className="btn-pill btn-dark text-xs" data-testid="cash-reopen-day"><Unlock size={11} /> Reopen Day</button>
          ) : null}
        </div>
      </header>

      {locked && !master && (
        <Banner tone="amber" testid="cash-locked-banner"><AlertCircle size={16} /> This day is closed. Ask the master account to reopen it.</Banner>
      )}

      {/* Yesterday → Today continuity warning */}
      {healthIssues.length > 0 && (master ? (
        <div className="rounded-2xl bg-red-50 border-2 border-red-300 p-5 mb-6" data-testid="cash-health-warning">
          <div className="flex items-center gap-2 mb-2"><ShieldAlert size={18} className="text-red-700" /><div className="font-medium text-red-800">Ledger continuity warning — {healthIssues.length} issue(s)</div></div>
          <ul className="text-sm text-red-700 space-y-1.5 mb-3">
            {healthIssues.map((i, idx) => (
              <li key={idx} className="flex items-start gap-2" data-testid={`cash-health-issue-${idx}`}>
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <span><strong>{i.date}</strong> — {i.type === "cash_carry_forward" ? "Cash" : "UPI"} opening mismatch: yesterday closed at {fmt(i.prev_close)}, this day opened at {fmt(i.today_open)} (diff ₹{Number(i.difference).toFixed(2)}).</span>
              </li>
            ))}
          </ul>
          <div className="flex gap-2 flex-wrap">
            <button onClick={fixCarry} className="btn-pill btn-dark text-xs" data-testid="cash-fix-carry-forward"><RefreshCcw size={11} /> Re-carry today's openings</button>
            <Link to="/admin/cash/history" className="btn-pill btn-ghost text-xs">Review in Cash History</Link>
          </div>
        </div>
      ) : (
        <Banner tone="amber" testid="cash-health-warning-staff"><ShieldAlert size={16} className="shrink-0" /> A ledger continuity issue was detected between yesterday and today. Please inform the Master before closing.</Banner>
      ))}

      {/* Live Current Position — always visible sticky-ish header */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="cash-live-position">
        <LiveTile label="Current Cash in Shop" value={day.current_cash_position} icon={Wallet} accent="emerald" testid="live-cash" />
        <LiveTile label="Current UPI Balance" value={day.closing_upi} icon={Smartphone} accent="blue" testid="live-upi" />
        <LiveTile label="Total Funds" value={Number(day.current_cash_position || 0) + Number(day.closing_upi || 0)} icon={IndianRupee} accent="charcoal" testid="live-total" muted />
      </div>

      {/* ---------- SECTION A — YESTERDAY / OPENING ---------- */}
      <Section step="A" title="Yesterday's Closing → Today's Opening" testid="section-opening">
        <div className="grid sm:grid-cols-2 gap-4">
          <ReadOnlyTile label="Yesterday's Closing Cash" value={day.opening_cash} icon={Coins} testid="yesterday-cash" />
          <ReadOnlyTile label="Yesterday's Closing UPI" value={day.opening_upi} icon={Smartphone} testid="yesterday-upi" />
        </div>
        <div className="mt-3 grid sm:grid-cols-2 gap-4">
          <ReadOnlyTile label="Today's Opening Cash" value={day.opening_cash} icon={Coins} tone="brand" testid="opening-cash" />
          <ReadOnlyTile label="Today's Opening UPI" value={day.opening_upi} icon={Smartphone} tone="brand" testid="opening-upi" />
        </div>
        <p className="mt-3 text-[11px] text-brand-charcoal/55">These carry forward automatically from yesterday's final closing. {master && <button onClick={fixCarry} className="underline text-brand-charcoal/70 hover:text-brand-charcoal" data-testid="reset-openings">Re-carry from yesterday</button>}</p>
      </Section>

      {/* ---------- SECTION B — TODAY'S SALES ---------- */}
      <Section step="B" title="Today's Sales" testid="section-sales">
        <div className="grid sm:grid-cols-3 gap-4">
          <InputTile label="Total Billing (from software)" value={day.total_billing} onSave={(v) => patchDay({ total_billing: v })} disabled={locked} testid="input-total-billing" />
          <InputTile label="UPI Sales" value={day.upi_sales} onSave={(v) => patchDay({ upi_sales: v })} disabled={locked} testid="input-upi-sales" />
          <ReadOnlyTile label="Cash Sales (auto)" value={day.cash_sales} icon={Calculator} tone="brand" testid="calc-cash-sales" />
        </div>
        <div className={`mt-3 rounded-xl border p-3 text-sm flex items-center gap-2 ${day.sales_matched ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-300 text-red-800"}`} data-testid="sales-validation">
          {day.sales_matched ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {day.sales_matched
            ? <>Cash + UPI = Total Billing · <strong>{fmt(day.cash_sales)} + {fmt(day.upi_sales)} = {fmt(day.total_billing)}</strong> · SALES MATCHED</>
            : <>SALES DO NOT MATCH · UPI sales ({fmt(day.upi_sales)}) exceed Total Billing ({fmt(day.total_billing)}) — difference {fmt(day.sales_difference)}</>}
        </div>
        <div className="mt-3 rounded-xl bg-brand-muted/40 border border-brand-line p-3 text-xs text-brand-charcoal/75">
          <strong>Cash flow so far:</strong> Yesterday's cash {fmt(day.opening_cash)} + Today's cash sales {fmt(day.cash_sales)} = <strong>Cash Available Before Expenses {fmt(Number(day.opening_cash || 0) + Number(day.cash_sales || 0))}</strong>
        </div>
      </Section>

      {/* ---------- SECTION C — TODAY'S EXPENSES ---------- */}
      <Section
        step="C"
        title={`Today's Expenses (${(day.transactions || []).filter((t) => t.type === "cash_expense" || t.type === "upi_expense" || t.type === "upi_purchase").length})`}
        testid="section-expenses"
        action={!locked && <button onClick={() => setShowExpense(true)} className="btn-pill btn-dark text-xs" data-testid="btn-add-expense"><Plus size={11} /> Add Expense</button>}
      >
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <MiniStat label="Cash Expenses today" value={day.cash_expenses} icon={Coins} minus />
          <MiniStat label="UPI Expenses today" value={Number(day.upi_expenses || 0) + Number(day.upi_purchases || 0)} icon={Smartphone} minus />
        </div>
        <TxList list={(day.transactions || []).filter((t) => ["cash_expense", "upi_expense", "upi_purchase"].includes(t.type))} onDelete={deleteTx} master={master} emptyText="No expenses recorded yet." />
      </Section>

      {/* ---------- SECTION D — CASH COUNT ---------- */}
      <Section step="D" title="Physical Cash Count (End-of-day denominations)" testid="section-count">
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-2 mb-3">
          {DENOMS.map((d) => (
            <div key={d}>
              <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">₹{d} × ?</label>
              <input type="number" min="0" disabled={locked && !master} value={day.denominations?.[String(d)] ?? 0} onChange={(e) => setDenom(d, e.target.value)} onBlur={saveDenoms} className="pp-input text-center font-mono" data-testid={`cash-denom-${d}`} />
              <div className="text-[10px] mt-0.5 text-brand-charcoal/50 text-center">= {fmt((day.denominations?.[String(d)] ?? 0) * d)}</div>
            </div>
          ))}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Coins (₹ total)</label>
            <input type="number" min="0" step="0.5" disabled={locked && !master} value={day.coins_extra ?? 0} onChange={(e) => setDay({ ...day, coins_extra: parseFloat(e.target.value || 0) })} onBlur={saveDenoms} className="pp-input text-center font-mono" data-testid="cash-coins-extra" />
            <div className="text-[10px] mt-0.5 text-brand-charcoal/50 text-center">Loose coins</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <MiniStat label="Denomination total" value={day.denom_total} icon={Wallet} />
          <MiniStat label="Coins" value={day.coins_extra} icon={Coins} />
          <MiniStat label="Physical Cash (counted)" value={day.physical_cash} icon={Wallet} strong />
        </div>
      </Section>

      {/* ---------- SECTION E — RECONCILIATION ---------- */}
      <Section step="E" title="Reconciliation — Expected vs Physical" testid="section-reconciliation">
        <div className="grid sm:grid-cols-3 gap-3">
          <MiniStat label="Expected Cash" value={day.expected_closing_cash} icon={Calculator} strong />
          <MiniStat label="Physical Cash" value={day.physical_cash} icon={Wallet} strong />
          <ReconTile diff={day.cash_difference} />
        </div>
        <div className="mt-3 text-xs text-brand-charcoal/65">
          Expected = Opening ({fmt(day.opening_cash)}) + Cash Sales ({fmt(day.cash_sales)}) + Cash Added ({fmt(day.cash_additions)}) − Cash Expenses ({fmt(day.cash_expenses)}) − Owner Cash Removed ({fmt(day.cash_withdrawals)}) = <strong>{fmt(day.expected_closing_cash)}</strong>
        </div>
      </Section>

      {/* ---------- SECTION F — OWNER CASH REMOVAL / ADDITION ---------- */}
      <Section
        step="F"
        title="Owner Cash Removal & UPI Transfers"
        testid="section-owner"
        action={!locked && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setShowOwner({ type: "cash_withdrawal", label: "Cash Removed from Shop" })} className="btn-pill btn-ghost text-xs" data-testid="btn-owner-cash-remove"><ArrowUpCircle size={11} /> Cash Out (Owner)</button>
            <button onClick={() => setShowOwner({ type: "cash_addition", label: "Cash Added to Shop" })} className="btn-pill btn-ghost text-xs" data-testid="btn-owner-cash-add"><ArrowDownCircle size={11} /> Cash In (Owner)</button>
            <button onClick={() => setShowOwner({ type: "upi_transfer", label: "UPI Transfer OUT (to Owner)" })} className="btn-pill btn-ghost text-xs" data-testid="btn-owner-upi-out"><ArrowUpCircle size={11} /> UPI Out</button>
            <button onClick={() => setShowOwner({ type: "upi_addition", label: "UPI Addition IN (from Owner)" })} className="btn-pill btn-ghost text-xs" data-testid="btn-owner-upi-in"><ArrowDownCircle size={11} /> UPI In</button>
          </div>
        )}
      >
        <div className="grid sm:grid-cols-2 gap-3">
          <MiniStat label="Owner Cash Removed" value={day.cash_withdrawals} icon={ArrowUpCircle} minus />
          <MiniStat label="Cash Added to Shop" value={day.cash_additions} icon={ArrowDownCircle} />
          <MiniStat label="UPI Transferred to Owner" value={day.owner_transfers} icon={ArrowUpCircle} minus />
          <MiniStat label="UPI Additions from Owner" value={day.owner_additions} icon={ArrowDownCircle} />
        </div>
        <TxList list={(day.transactions || []).filter((t) => ["cash_withdrawal", "cash_addition", "upi_transfer", "upi_addition"].includes(t.type))} onDelete={deleteTx} master={master} emptyText="No owner movements recorded." />
      </Section>

      {/* ---------- SECTION G — FINAL CLOSING SUMMARY ---------- */}
      <Section step="G" title="Final Closing Summary" testid="section-closing">
        <div className="grid md:grid-cols-2 gap-4">
          <StorySummary
            title="Cash Movement"
            rows={[
              ["Yesterday Closing", day.opening_cash, "="],
              ["+ Today's Cash Sales", day.cash_sales, "+"],
              ["+ Cash Added", day.cash_additions, "+"],
              ["− Cash Expenses", day.cash_expenses, "−"],
              ["− Owner Cash Removed", day.cash_withdrawals, "−"],
            ]}
            total={["Final Closing Cash (expected)", day.expected_closing_cash]}
            actual={["Physical Cash Counted", day.physical_cash]}
            diff={day.cash_difference}
            testid="story-cash"
          />
          <StorySummary
            title="UPI Movement"
            rows={[
              ["Yesterday Closing", day.opening_upi, "="],
              ["+ Today's UPI Sales", day.upi_sales, "+"],
              ["+ UPI Additions from Owner", day.owner_additions, "+"],
              ["− UPI Expenses", day.upi_expenses, "−"],
              ["− UPI Purchases", day.upi_purchases, "−"],
              ["− UPI to Owner", day.owner_transfers, "−"],
            ]}
            total={["Final Closing UPI", day.closing_upi]}
            testid="story-upi"
          />
        </div>
        {!day.can_close && !locked && (
          <div className="mt-4 rounded-xl border-2 border-red-300 bg-red-50 p-4" data-testid="close-blockers">
            <div className="text-red-800 font-medium mb-1 flex items-center gap-2"><Lock size={14} /> Cannot close day yet</div>
            <ul className="text-sm text-red-700 list-disc pl-5">
              {(day.close_blockers || []).map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </div>
        )}
      </Section>

      {/* ---------- SECTION H — NEXT DAY CARRY FORWARD PREVIEW ---------- */}
      <Section step="H" title="Next Day Carry Forward (Preview)" testid="section-carry">
        <div className="grid sm:grid-cols-2 gap-4">
          <ReadOnlyTile label="Tomorrow's Opening Cash" value={day.expected_closing_cash} icon={Coins} tone="brand" testid="tomorrow-cash" />
          <ReadOnlyTile label="Tomorrow's Opening UPI" value={day.closing_upi} icon={Smartphone} tone="brand" testid="tomorrow-upi" />
        </div>
        <p className="mt-3 text-[11px] text-brand-charcoal/55">These become tomorrow's opening balances the moment you close today.</p>
      </Section>

      {/* ---------- MONEY MOVEMENT TIMELINE ---------- */}
      <Section
        step="•"
        title="Money Movement (chronological)"
        testid="section-timeline"
        action={<button onClick={() => setShowTimeline((s) => !s)} className="btn-pill btn-ghost text-xs" data-testid="toggle-timeline">{showTimeline ? <ChevronUp size={11} /> : <ChevronDown size={11} />} {showTimeline ? "Hide" : "Show"}</button>}
      >
        {showTimeline && <Timeline day={day} />}
      </Section>

      {/* ---------- MODALS ---------- */}
      {showExpense && <ExpenseModal date={day.date} onClose={() => setShowExpense(false)} onDone={(dayData) => { setDay(dayData); setShowExpense(false); }} />}
      {showOwner && <OwnerModal date={day.date} type={showOwner.type} label={showOwner.label} onClose={() => setShowOwner(null)} onDone={(dayData) => { setDay(dayData); setShowOwner(null); }} />}
    </div>
  );
}

/* ---------- Building blocks ---------- */
function Section({ step, title, testid, action, children }) {
  return (
    <section className="rounded-2xl bg-white border border-brand-line p-5 mb-5" data-testid={testid}>
      <header className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-baseline gap-3">
          <span className="text-[10px] uppercase tracking-[0.24em] text-brand-gold font-medium">Step {step}</span>
          <h2 className="font-display text-xl">{title}</h2>
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}
function ReadOnlyTile({ label, value, icon: Icon, tone = "default", testid }) {
  const bg = tone === "brand" ? "bg-brand-muted/40 border-brand-gold/40" : "bg-white border-brand-line";
  return (
    <div className={`rounded-xl border ${bg} p-4`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 inline-flex items-center gap-1">{Icon && <Icon size={11} />} {label}</div>
      <div className="font-mono text-2xl font-display mt-1">{fmt(value)}</div>
    </div>
  );
}
function LiveTile({ label, value, icon: Icon, accent = "emerald", testid, muted }) {
  const colours = { emerald: "bg-emerald-50 border-emerald-200 text-emerald-800", blue: "bg-sky-50 border-sky-200 text-sky-800", charcoal: "bg-brand-charcoal text-white border-brand-charcoal" };
  return (
    <div className={`rounded-2xl border-2 p-4 ${muted ? colours.charcoal : colours[accent]}`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-widest inline-flex items-center gap-1 ${muted ? "text-white/70" : ""}`}><Icon size={11} /> {label}</div>
      <div className="font-mono text-2xl font-display mt-1">{fmt(value)}</div>
    </div>
  );
}
function InputTile({ label, value, onSave, disabled, testid }) {
  const [v, setV] = useState(String(value ?? 0));
  useEffect(() => { setV(String(value ?? 0)); }, [value]);
  return (
    <div className="rounded-xl border border-brand-line bg-white p-4" data-testid={testid}>
      <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">{label}</label>
      <input type="number" step="0.01" min="0" disabled={disabled} value={v} onChange={(e) => setV(e.target.value)} onBlur={() => Number(v) !== Number(value) && onSave(parseFloat(v || 0))} className="pp-input text-right font-mono text-xl mt-1" />
    </div>
  );
}
function MiniStat({ label, value, icon: Icon, minus, strong }) {
  return (
    <div className="rounded-xl border border-brand-line px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 inline-flex items-center gap-1">{Icon && <Icon size={10} />} {label}</div>
      <div className={`font-mono mt-0.5 ${strong ? "text-xl font-display" : "text-base"}`}>{minus ? "−" : ""}{fmt(value)}</div>
    </div>
  );
}
function ReconTile({ diff }) {
  const zero = Math.abs(Number(diff || 0)) < 0.01;
  const short = Number(diff) < -0.01;
  const style = zero ? "bg-emerald-50 border-emerald-300 text-emerald-800" : short ? "bg-red-50 border-red-300 text-red-800" : "bg-amber-50 border-amber-300 text-amber-800";
  return (
    <div className={`rounded-xl border-2 px-3 py-2 ${style}`} data-testid="cash-reconciliation">
      <div className="text-[10px] uppercase tracking-widest inline-flex items-center gap-1">{zero ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />} {zero ? "Cash Matched" : short ? "Cash Shortage" : "Cash Excess"}</div>
      <div className="font-mono mt-0.5 text-xl font-display">{fmt(Math.abs(Number(diff || 0)))}</div>
    </div>
  );
}
function Banner({ tone, children, testid }) {
  const cls = tone === "amber" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-red-50 border-red-200 text-red-800";
  return <div className={`rounded-2xl border p-4 mb-6 text-sm flex items-center gap-2 ${cls}`} data-testid={testid}>{children}</div>;
}
function StatusBadge({ status }) {
  if (status === "closed") return <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 inline-flex items-center gap-1"><Lock size={9} /> Closed</span>;
  return <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={9} /> Open</span>;
}
function StorySummary({ title, rows, total, actual, diff, testid }) {
  return (
    <div className="rounded-xl border border-brand-line bg-brand-muted/25 p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 mb-3">{title}</div>
      <ul className="space-y-1 text-sm">
        {rows.map(([label, val, sign], i) => (
          <li key={i} className="flex items-center justify-between border-b border-brand-line/60 py-1">
            <span className="text-brand-charcoal/75">{label}</span>
            <span className="font-mono">{sign === "−" ? "−" : ""}{fmt(val)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex items-center justify-between font-medium">
        <span>{total[0]}</span>
        <span className="font-mono text-lg">{fmt(total[1])}</span>
      </div>
      {actual && (
        <>
          <div className="mt-2 flex items-center justify-between text-sm">
            <span className="text-brand-charcoal/75">{actual[0]}</span>
            <span className="font-mono">{fmt(actual[1])}</span>
          </div>
          <div className={`mt-1 text-xs font-medium ${Math.abs(Number(diff || 0)) < 0.01 ? "text-emerald-700" : Number(diff) < 0 ? "text-red-700" : "text-amber-700"}`}>
            {Math.abs(Number(diff || 0)) < 0.01 ? "🟢 Cash matched" : Number(diff) < 0 ? `🔴 Short by ${fmt(Math.abs(diff))}` : `🟠 Excess by ${fmt(diff)}`}
          </div>
        </>
      )}
    </div>
  );
}
const TX_LABEL = {
  cash_expense: "Cash Expense",
  cash_addition: "Cash Added",
  cash_withdrawal: "Owner Cash Removal",
  upi_expense: "UPI Expense",
  upi_purchase: "UPI Purchase",
  upi_transfer: "UPI → Owner",
  upi_addition: "UPI ← Owner",
};
function TxList({ list, onDelete, master, emptyText }) {
  if (!list || list.length === 0) return <div className="text-center text-brand-charcoal/50 py-4 text-sm">{emptyText}</div>;
  return (
    <ul className="divide-y divide-brand-line/60 mt-2">
      {list.map((t) => {
        const isMinus = ["cash_expense", "cash_withdrawal", "upi_expense", "upi_purchase", "upi_transfer"].includes(t.type);
        return (
          <li key={t.id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`tx-row-${t.id}`}>
            <div className="min-w-0">
              <div className="font-medium">{TX_LABEL[t.type] || t.type}{t.category && <span className="text-brand-charcoal/55"> · {t.category}</span>}</div>
              <div className="text-xs text-brand-charcoal/60 truncate">{t.note || "—"} · {t.created_by} · {new Date(t.created_at).toLocaleTimeString()}</div>
              {t.receipt_path && <ReceiptThumb path={t.receipt_path} />}
            </div>
            <div className={`font-mono ${isMinus ? "text-red-700" : "text-emerald-700"}`}>{isMinus ? "−" : "+"}{fmt(t.amount)}</div>
            {master && <button onClick={() => onDelete(t.id)} className="text-red-600 hover:bg-red-50 rounded p-1" data-testid={`tx-del-${t.id}`}><Trash size={12} /></button>}
          </li>
        );
      })}
    </ul>
  );
}
function ReceiptThumb({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const res = await api.get(`/admin/cash/receipts/${path}`, { responseType: "blob" });
        if (live) setUrl(URL.createObjectURL(res.data));
      } catch { /* ignore */ }
    };
    load();
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line
  }, [path]);
  if (!url) return <span className="text-[10px] text-brand-charcoal/45 inline-flex items-center gap-0.5"><Receipt size={9} /> Receipt attached</span>;
  return <a href={url} target="_blank" rel="noreferrer" className="inline-block mt-1"><img src={url} alt="Receipt" className="max-h-14 rounded border border-brand-line" /></a>;
}
function Timeline({ day }) {
  const items = [];
  items.push({ time: null, label: "Opening Cash", value: day.opening_cash, sign: "=", type: "cash" });
  items.push({ time: null, label: "Opening UPI", value: day.opening_upi, sign: "=", type: "upi" });
  (day.transactions || []).forEach((t) => {
    const isMinus = ["cash_expense", "cash_withdrawal", "upi_expense", "upi_purchase", "upi_transfer"].includes(t.type);
    items.push({ time: t.created_at, label: (TX_LABEL[t.type] || t.type) + (t.category ? ` — ${t.category}` : "") + (t.note ? ` (${t.note})` : ""), value: t.amount, sign: isMinus ? "−" : "+", type: t.type.startsWith("upi") ? "upi" : "cash" });
  });
  if (Number(day.total_billing) > 0) {
    items.push({ time: null, label: `Total Billing entered (Cash sales ${fmt(day.cash_sales)}, UPI sales ${fmt(day.upi_sales)})`, value: day.total_billing, sign: "=", type: "sale" });
  }
  return (
    <ol className="relative border-l border-brand-line pl-4 space-y-2">
      {items.map((i, idx) => (
        <li key={idx} className="text-sm">
          <div className="text-[11px] text-brand-charcoal/55">{i.time ? new Date(i.time).toLocaleTimeString() : "—"}</div>
          <div className="flex items-center justify-between">
            <span>{i.label}</span>
            <span className={`font-mono ${i.sign === "−" ? "text-red-700" : i.sign === "+" ? "text-emerald-700" : "text-brand-charcoal/70"}`}>{i.sign !== "=" ? i.sign : ""}{fmt(i.value)}</span>
          </div>
        </li>
      ))}
    </ol>
  );
}

/* ---------- Expense modal (with receipt upload) ---------- */
function ExpenseModal({ date, onClose, onDone }) {
  const [method, setMethod] = useState("cash"); // cash | upi
  const [category, setCategory] = useState(EXPENSE_CATEGORIES[0]);
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState("");
  const [receiptPath, setReceiptPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/cash/receipts/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReceiptPath(data.receipt_path); toast.success("Receipt uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setSubmitting(true);
    try {
      const type = method === "cash" ? "cash_expense" : "upi_expense";
      const { data } = await api.post("/admin/cash/transactions", { date, type, amount: amt, note, category, receipt_path: receiptPath });
      toast.success("Expense recorded"); onDone(data.day);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };
  return (
    <ModalShell title="Add Expense" onClose={onClose} testid="expense-modal">
      <div className="space-y-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 mb-1">Payment Method</div>
          <div className="flex gap-2">
            {[["cash", "Cash", Coins], ["upi", "UPI", Smartphone]].map(([k, l, Icn]) => (
              <button key={k} onClick={() => setMethod(k)} className={`btn-pill text-xs ${method === k ? "btn-dark" : "btn-ghost"}`} data-testid={`expense-method-${k}`}><Icn size={11} /> {l}</button>
            ))}
          </div>
        </div>
        <Field label="Category">
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="pp-input" data-testid="expense-category">
            {EXPENSE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Description"><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Bought milk from supplier" className="pp-input" data-testid="expense-note" /></Field>
        <Field label="Amount (₹)"><input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="pp-input" data-testid="expense-amount" /></Field>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 mb-1">Receipt (optional)</div>
          <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-pill btn-ghost text-xs" data-testid="expense-upload"><Upload size={11} /> {uploading ? "Uploading…" : receiptPath ? "Replace photo" : "Attach photo"}</button>
          {receiptPath && <span className="ml-2 text-[11px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={10} /> Attached</span>}
        </div>
        <div className="pt-2 flex justify-end gap-2">
          <button onClick={onClose} className="btn-pill btn-ghost text-xs">Cancel</button>
          <button onClick={submit} disabled={submitting} className="btn-pill btn-dark text-xs" data-testid="expense-submit">{submitting ? "Saving…" : "Save Expense"}</button>
        </div>
      </div>
    </ModalShell>
  );
}

/* ---------- Owner Movement modal ---------- */
function OwnerModal({ date, type, label, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState(OWNER_REASONS[0]);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/cash/transactions", { date, type, amount: amt, note, category: reason });
      toast.success("Recorded"); onDone(data.day);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };
  return (
    <ModalShell title={label} onClose={onClose} testid="owner-modal">
      <div className="space-y-3">
        <Field label="Amount (₹)"><input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="pp-input" data-testid="owner-amount" /></Field>
        <Field label="Reason">
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="pp-input" data-testid="owner-reason">
            {OWNER_REASONS.map((r) => <option key={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Description (optional)"><input value={note} onChange={(e) => setNote(e.target.value)} className="pp-input" data-testid="owner-note" placeholder="e.g. Owner collection for bank deposit" /></Field>
        <div className="pt-2 flex justify-end gap-2">
          <button onClick={onClose} className="btn-pill btn-ghost text-xs">Cancel</button>
          <button onClick={submit} disabled={submitting} className="btn-pill btn-dark text-xs" data-testid="owner-submit">{submitting ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </ModalShell>
  );
}
function Field({ label, children }) {
  return (<div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 block mb-1">{label}</label>{children}</div>);
}
function ModalShell({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className="max-w-md w-full bg-white rounded-2xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-xl">{title}</div>
          <button onClick={onClose} className="text-brand-charcoal/60 hover:text-brand-charcoal"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
